// Рендер контента карточки (см. docs/cards_format.txt) в HTML и plain text.
// Формат: массив блоков вида
//   { type: "paragraph" | "heading" | "quote" | "code" | "list" | "image" | "embed" | "divider", ... }
// Часть блоков содержит text + entities: [{ type, offset, length, url? }].

const ENTITY_TAG = {
    bold: "strong",
    italic: "em",
    underline: "u",
    strike: "s",
    code: "code",
};

function escapeHtml(str) {
    if (str == null) return "";
    return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function escapeAttr(str) {
    return escapeHtml(str);
}

// Наивная обёртка текста entities-разметкой. Работает корректно, если разметка
// корректно вложена (без пересечений разных типов).
function applyEntities(text, entities) {
    const safeText = String(text ?? "");
    if (!entities || !entities.length) return escapeHtml(safeText);

    // Собираем точки открытия/закрытия. Закрытия выводятся раньше открытий
    // в одной позиции — это обеспечивает симметричную вложенность.
    const markers = [];
    entities.forEach((e, idx) => {
        const start = Math.max(0, Number(e.offset) || 0);
        const end = start + Math.max(0, Number(e.length) || 0);
        markers.push({ pos: start, kind: "open", entity: e, order: idx });
        markers.push({ pos: end, kind: "close", entity: e, order: idx });
    });
    markers.sort((a, b) => {
        if (a.pos !== b.pos) return a.pos - b.pos;
        if (a.kind !== b.kind) return a.kind === "close" ? -1 : 1;
        return a.kind === "open" ? a.order - b.order : b.order - a.order;
    });

    let out = "";
    let cursor = 0;
    for (const m of markers) {
        if (m.pos > cursor) {
            out += escapeHtml(safeText.slice(cursor, m.pos));
            cursor = m.pos;
        }
        out += entityTag(m.entity, m.kind === "open");
    }
    if (cursor < safeText.length) out += escapeHtml(safeText.slice(cursor));
    return out;
}

function entityTag(entity, open) {
    if (entity.type === "link") {
        if (!open) return "</a>";
        const href = escapeAttr(entity.url || "");
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">`;
    }
    const tag = ENTITY_TAG[entity.type];
    if (!tag) return "";
    return open ? `<${tag}>` : `</${tag}>`;
}

function renderBlock(block) {
    if (!block || typeof block !== "object") return "";
    switch (block.type) {
        case "paragraph":
            return `<p>${applyEntities(block.text, block.entities)}</p>`;
        case "heading": {
            const level = Math.min(6, Math.max(1, Number(block.level) || 1));
            return `<h${level}>${applyEntities(block.text, block.entities)}</h${level}>`;
        }
        case "quote":
            return `<blockquote>${applyEntities(block.text, block.entities)}</blockquote>`;
        case "code": {
            const lang = block.language ? ` class="language-${escapeAttr(block.language)}"` : "";
            return `<pre><code${lang}>${escapeHtml(block.text)}</code></pre>`;
        }
        case "list": {
            const tag = block.ordered ? "ol" : "ul";
            const items = Array.isArray(block.items) ? block.items : [];
            const inner = items
                .map((it) => `<li>${applyEntities(it?.text, it?.entities)}</li>`)
                .join("");
            return `<${tag}>${inner}</${tag}>`;
        }
        case "image": {
            const src = escapeAttr(block.url || "");
            const alt = escapeAttr(block.alt || "");
            return `<img src="${src}" alt="${alt}" />`;
        }
        case "embed": {
            const src = escapeAttr(block.url || "");
            if (block.kind === "video") {
                return `<iframe src="${src}" frameborder="0" allowfullscreen></iframe>`;
            }
            return `<a href="${src}" target="_blank" rel="noopener noreferrer">${escapeHtml(block.url || "")}</a>`;
        }
        case "divider":
            return "<hr />";
        default:
            return "";
    }
}

// Рендер массива блоков в HTML. Пустой/некорректный вход → пустая строка.
export function blocksToHtml(content) {
    const blocks = normalizeContent(content);
    if (!blocks.length) return "";
    return blocks.map(renderBlock).join("");
}

// Извлекает плоский текст (для списков-превью, для translate и т.п.).
export function blocksToText(content) {
    const blocks = normalizeContent(content);
    if (!blocks.length) return "";
    const parts = [];
    for (const b of blocks) {
        if (!b) continue;
        if (b.type === "list" && Array.isArray(b.items)) {
            b.items.forEach((it) => it?.text && parts.push(String(it.text)));
        } else if (typeof b.text === "string") {
            parts.push(b.text);
        } else if (b.type === "image" && b.alt) {
            parts.push(String(b.alt));
        }
    }
    return parts.join(" ").trim();
}

function normalizeContent(content) {
    if (Array.isArray(content)) return content;
    if (typeof content === "string" && content.length) {
        try {
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed)) return parsed;
        } catch (_) {
            return [];
        }
    }
    return [];
}
